/**
 * Scoped KPI filters — replace the audience segmented control.
 *
 * Each card counts only events in its metric scope (supply / demand / account).
 * Demand metrics honor optional MF internal and partner-agent exclusion toggles.
 */

import {
  buildAudienceContext,
  classifyRowAudience,
  buildInternalExclusion,
  isInternalRow,
  mergeFeedRows,
} from './visitorSegregation';
import { METRIC_SCOPES, rowEventCategory, inferActorRole } from '../../utils/eventAttribution';

export function rowIsAgentActivity(row, ctx) {
  const role = row?.detail?.actorRole || inferActorRole(row?.meta, row?.detail);
  if (role === 'agent') return true;
  if (role === 'mf_staff') return false;
  if (ctx) return classifyRowAudience(row, ctx) === 'agents';
  return false;
}

export function rowIsExcludedInternal(row, exclusion) {
  if (!exclusion) return false;
  return isInternalRow(row, exclusion);
}

/**
 * @param {Array} rows
 * @param {{
 *   scope: 'supply'|'demand'|'account',
 *   excludeInternal?: boolean,
 *   excludeAgentActivity?: boolean,
 *   partnerHostname?: string,
 *   partnerAdminUsers?: Array,
 *   contextRows?: Array,
 * }} opts
 */
export function filterRowsByMetricScope(rows, opts = {}) {
  const {
    scope,
    excludeInternal = false,
    excludeAgentActivity = false,
    partnerHostname = '',
    partnerAdminUsers = [],
    contextRows = null,
  } = opts;

  if (!scope || !Array.isArray(rows)) return rows || [];

  let result = rows.filter((row) => rowEventCategory(row) === scope);

  const ctxSource =
    Array.isArray(contextRows) && contextRows.length > 0 ? contextRows : rows;
  const ctx =
    excludeAgentActivity && scope === METRIC_SCOPES.demand
      ? buildAudienceContext(ctxSource, partnerHostname, { partnerAdminUsers })
      : null;

  if (excludeAgentActivity && scope === METRIC_SCOPES.demand && ctx) {
    result = result.filter((row) => !rowIsAgentActivity(row, ctx));
  }

  if (excludeInternal && scope !== METRIC_SCOPES.supply) {
    const exclusion = buildInternalExclusion(mergeFeedRows(ctxSource, rows), {
      partnerAdminUsers,
    });
    result = result.filter((row) => !rowIsExcludedInternal(row, exclusion));
  }

  return result;
}

export function applyMetricScopeFilters(rows, opts = {}) {
  const toFilter = Array.isArray(rows) ? rows : [];
  const { scope } = opts;
  if (!scope) return toFilter;
  return filterRowsByMetricScope(toFilter, opts);
}

export function metricScopeFilterLabel(
  scope,
  excludeInternal,
  excludePartnerAgents = false,
  partnerAgentEmailDomain = null
) {
  const parts = [];
  if (scope === METRIC_SCOPES.supply) parts.push('Pipeline activity');
  else if (scope === METRIC_SCOPES.demand) parts.push('Buyer engagement');
  else if (scope === METRIC_SCOPES.account) parts.push('Account sign-ups');
  if (excludePartnerAgents && scope === METRIC_SCOPES.demand && partnerAgentEmailDomain) {
    parts.push(`${partnerAgentEmailDomain} excluded`);
  }
  if (excludeInternal && scope !== METRIC_SCOPES.supply) {
    parts.push('@magicfurnish.com excluded');
  }
  return parts.join(' · ');
}

export function isMetricScopeFilterActive(
  scope,
  excludeInternal,
  excludePartnerAgents = false
) {
  if (scope === METRIC_SCOPES.demand) {
    return Boolean(excludeInternal || excludePartnerAgents);
  }
  if (scope === METRIC_SCOPES.account && excludeInternal) return true;
  return false;
}
